import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

/**
 * Testing Center v1 — active testing-session pointer.
 *
 * Holds WHICH test the floating overlay is pinned to (suite + test number) plus
 * overlay chrome state (minimized, drag offset). The test CONTENT comes from
 * the generated suites JSON and the RESULTS (checkmarks, pass/fail) live on the
 * server via TanStack Query — this store is only the session pointer, so it
 * survives route changes for free and a reload restores the pointer from
 * sessionStorage while the checkmarks re-hydrate from the server.
 *
 * v2 extension point (auto-play / guided advance — OUT of scope for v1): add a
 * `mode: 'manual' | 'auto'` field here plus an effect that watches step-check
 * completion and calls `next()` — the overlay already routes all navigation
 * through next()/prev()/goToTest(), so auto-advance plugs in without touching
 * the components.
 */

export interface TestingSessionState {
  /** Active suite id, or null when no session is running. */
  suiteId: string | null;
  /** Active test number within the suite. */
  testNumber: number | null;
  /** Overlay collapsed to a pill. */
  minimized: boolean;
  /** Drag offset in px from the default bottom-right anchor. */
  offset: { x: number; y: number };

  startSession: (suiteId: string, testNumber: number) => void;
  endSession: () => void;
  goToTest: (testNumber: number) => void;
  /** Move to the next test given the suite's ordered test numbers. */
  next: (orderedNumbers: number[]) => void;
  /** Move to the previous test given the suite's ordered test numbers. */
  prev: (orderedNumbers: number[]) => void;
  setMinimized: (minimized: boolean) => void;
  setOffset: (offset: { x: number; y: number }) => void;
}

const step = (
  current: number | null,
  orderedNumbers: number[],
  direction: 1 | -1,
): number | null => {
  if (current === null || orderedNumbers.length === 0) return current;
  const idx = orderedNumbers.indexOf(current);
  if (idx === -1) return orderedNumbers[0];
  const nextIdx = idx + direction;
  if (nextIdx < 0 || nextIdx >= orderedNumbers.length) return current; // clamp at ends
  return orderedNumbers[nextIdx];
};

export const useTestingSessionStore = create<TestingSessionState>()(
  devtools(
    persist(
      (set, get) => ({
        suiteId: null,
        testNumber: null,
        minimized: false,
        offset: { x: 0, y: 0 },

        startSession: (suiteId, testNumber) =>
          set({ suiteId, testNumber, minimized: false }, false, 'testing/startSession'),

        endSession: () =>
          set(
            { suiteId: null, testNumber: null, minimized: false, offset: { x: 0, y: 0 } },
            false,
            'testing/endSession',
          ),

        goToTest: (testNumber) => set({ testNumber }, false, 'testing/goToTest'),

        next: (orderedNumbers) =>
          set(
            { testNumber: step(get().testNumber, orderedNumbers, 1) },
            false,
            'testing/next',
          ),

        prev: (orderedNumbers) =>
          set(
            { testNumber: step(get().testNumber, orderedNumbers, -1) },
            false,
            'testing/prev',
          ),

        setMinimized: (minimized) => set({ minimized }, false, 'testing/setMinimized'),

        setOffset: (offset) => set({ offset }, false, 'testing/setOffset'),
      }),
      {
        name: 'testing-session-store',
        version: 1,
        // sessionStorage on purpose: a testing session belongs to the tab/session
        // that started it; a reload restores it, a fresh browser session doesn't
        // resurrect a stale overlay.
        storage: createJSONStorage(() => sessionStorage),
      },
    ),
    { name: 'testing-session-store' },
  ),
);
